<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import type { ICreditCard } from '~/components/entity/credit/item/types'
import type { IBestOfferCredit } from '~/components/entity/best-offer/credit'
import type { SearchResponse } from 'meilisearch'
import { ContentBlock } from '~/components/ui/content-block'
import { Heading } from '~/components/ui/heading'
import { EntityBestOfferCredit } from '~/components/entity/best-offer/credit'
import { EntityBestOfferCreditSkeleton } from '~/components/entity/best-offer/credit/skeleton'
import { WidgetBestOfferFilters } from './filters'

const config = useRuntimeConfig()
const { locale, t } = useI18n()

// Filter state
const amount = ref<number | undefined>(500000)
const currency = ref<string>('kgs')
const period = ref<string>('12')
const purpose = ref<string>('0')
const forWhom = ref<string>('private_person')

// More options state
const showMore = ref(false)

// Debounced values
const debouncedAmount = useDebounce(amount, 500)

// Currency list for filters
const currencyList = useCurrencyStore().list

// Build active filter for MeiliSearch
const activeFilter = computed(() => {
  const allFilters: string[] = []

  if (debouncedAmount.value && currency.value && currency.value !== 'any') {
    const amt = debouncedAmount.value
    const baseCondition = `(settings.min_amount_${currency.value} <= ${amt} AND (settings.max_amount_${currency.value} >= ${amt} OR settings.max_amount_${currency.value} = 0))`
    allFilters.push(`(${baseCondition})`)
  }

  if (period.value && period.value !== '0') {
    const periodVal = period.value
    let combined = `rates.period >= ${periodVal}`

    if (currency.value && currency.value !== 'any') {
      const curId = currencyList.find((el) => el.code.toLowerCase() === currency.value)?.id
      if (curId) {
        combined = `(${combined} AND rates.currency_id = ${curId})`
      }
    }

    allFilters.push(combined)
  }

  if (purpose.value && purpose.value !== '0') {
    allFilters.push(`purposes.id = ${purpose.value}`)
  }

  if (currency.value && currency.value !== 'any') {
    allFilters.push(`settings.is_${currency.value} = true`)
  }

  if (forWhom.value && forWhom.value !== '0') {
    allFilters.push(`for_whom.${forWhom.value} = true`)
  }

  // Filter only credits that allow credit requests
  allFilters.push('is_credit_request_allowed = true')

  return allFilters.length ? [`(${allFilters.join(' AND ')})`] : undefined
})

// When a specific currency is selected, use that currency's values instead of the general ones
const activeCurrency = computed(() =>
  currency.value && currency.value !== 'any' ? currency.value : undefined
)

const { settingKey, localizedSetting, numericSetting } = useMeiliCurrencySettings(activeCurrency)

// Search key for cache
const searchKey = 'best-offer-credits-search'

// MeiliSearch fetch
const {
  data: result,
  pending,
  refresh
} = await useAsyncData<SearchResponse<ICreditCard> | null>(searchKey, async () => {
  const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_credits`)

  // Sort by the selected currency's rate value, falling back to the general one
  const minRateValueSort = `settings.${settingKey('min_rate_value')}:asc`

  try {
    const searchResult = (await search('', {
      hitsPerPage: 5,
      page: 1,
      sort: [minRateValueSort, 'weight:desc', 'highlight:desc', 'updated_at:desc'],
      ...(activeFilter.value ? { filter: activeFilter.value } : {})
    })) as unknown as SearchResponse<ICreditCard>

    return searchResult
  } catch (e) {
    console.error('MeiliSearch fetch error:', e)
    return null
  }
})

// Calculate monthly payment (annuity formula)
const calculateMonthlyPayment = (amount: number, rate: number, periodMonths: number): number => {
  if (!amount || !periodMonths) return 0
  // If rate is 0, simply divide amount by months (no interest)
  if (rate === 0) return amount / periodMonths
  const monthlyRate = rate / 12 / 100
  const annuity =
    (monthlyRate * Math.pow(1 + monthlyRate, periodMonths)) /
    (Math.pow(1 + monthlyRate, periodMonths) - 1)
  return amount * annuity
}

// Calculate overpayment
const calculateOverpayment = (
  monthlyPayment: number,
  periodMonths: number,
  amount: number
): number => {
  return Math.round(monthlyPayment * periodMonths - amount)
}

// Transform MeiliSearch results to IBestOfferCredit format
const transformedResults = computed(() => {
  if (!result.value?.hits) return []

  const minRateValueKey = settingKey('min_rate_value')
  const minRateKey = settingKey('min_rate')
  const maxPeriodKey = settingKey('max_period')

  return result.value.hits.map((hit) => {
    const orgTitle = hit.organization.title?.[locale.value] || ''
    const orgLogo = hit.organization.logo || ''
    const orgUrl = hit.organization.url || ''
    const creditUrl = hit.url || ''

    // Calculate monthly payment and overpayment
    const rate = numericSetting(hit.settings, minRateValueKey)
    const amt = amount.value || 0
    const months = parseInt(period.value) || 0
    const currencyCode = currency.value || 'kgs'

    const monthlyPayment = calculateMonthlyPayment(amt, rate, months)
    const overpayment = rate > 0 ? calculateOverpayment(monthlyPayment, months, amt) : 0
    const currencyId = useGetCurrencyIdByCode(currencyCode) || 1

    return {
      bank: {
        logo: orgLogo,
        title: orgTitle,
        rating: hit.organization.rating || 0,
        url: orgUrl
      },
      title: hit.title?.[locale.value] || '',
      url: creditUrl,
      rate: localizedSetting(hit.settings, minRateKey) ?? '',
      monthlyPayment:
        monthlyPayment > 0 ? useCurrencyFormat(monthlyPayment, currencyId, true) : '—',
      overpayment: overpayment > 0 ? useCurrencyFormat(overpayment, currencyId, true) : '—',
      term: localizedSetting(hit.settings, maxPeriodKey) ?? '',
      // Pass current filter values
      amount: amount.value,
      currency: currency.value,
      period: period.value,
      purpose: purpose.value
    } as IBestOfferCredit
  })
})

// First result (highlighted)
const firstResult = computed(() => transformedResults.value[0])

// Remaining results (for "more options")
const remainingResults = computed(() => transformedResults.value.slice(1))

// Watch for filter changes and refresh
watch(
  [debouncedAmount, currency, period, purpose, forWhom],
  () => {
    refresh()
  },
  {}
)

// Toggle more options
const toggleShowMore = () => {
  showMore.value = !showMore.value
}
</script>

<template>
  <div class="space-y-4">
    <!-- Header with title -->
    <Heading tag="h2">{{ t('common.best_offer.title') }}</Heading>

    <!-- Content -->
    <ContentBlock variant="white">
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <!-- Filters column (left) -->
        <div class="lg:col-span-1">
          <WidgetBestOfferFilters
            v-model:amount="amount"
            v-model:currency="currency"
            v-model:period="period"
            v-model:purpose="purpose"
            v-model:for-whom="forWhom"
          />
        </div>

        <!-- Best offer display column (right) -->
        <div class="space-y-4 lg:col-span-2">
          <!-- Loading state -->
          <EntityBestOfferCreditSkeleton v-if="pending" />

          <!-- First result (highlighted) -->
          <EntityBestOfferCredit
            v-else-if="firstResult"
            :data="firstResult"
            :highlighted="true"
            :show-title="true"
          />
          <div v-if="showMore && remainingResults.length > 0" class="grid grid-cols-1 gap-3">
            <EntityBestOfferCredit
              v-for="(item, index) in remainingResults"
              :key="index"
              :data="item"
              :highlighted="false"
              :show-title="false"
            />
          </div>

          <!-- Show more / collapse link -->
          <div
            v-if="!pending && remainingResults.length > 0"
            class="flex justify-center"
          >
            <button
              class="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              @click="toggleShowMore"
            >
              {{ showMore ? t('common.best_offer.collapse') : t('common.best_offer.show_all') }}
            </button>
          </div>

          <!-- No results -->
          <div v-else-if="!pending && !firstResult" class="flex items-center justify-center p-8">
            <span class="text-gray-500">{{ t('common.best_offer.no_results') }}</span>
          </div>
        </div>
      </div>
      <!-- Remaining results (hidden by default) -->
    </ContentBlock>
  </div>
</template>
